// <chat-switcher> — a custom element that replaces the native <select> for
// the chat drawer's conversation picker. Unlike a <select>, each row can
// show a real avatar (the Jarvis knowledge-graph orb, an emoji, or an
// initial) and a sub-label.
//
// Usage:
//   <chat-switcher value="jarvis">
//     <script type="application/json">
//       { "groups": [ { "label": "Assistant",
//                       "items": [ { "key":"jarvis", "label":"Jarvis",
//                                    "sublabel":"AI exec assistant",
//                                    "avatar":"orb" } ] } ] }
//     </script>
//   </chat-switcher>
//
// avatar is one of: "orb" | any emoji string | "#<letter>" (initial chip).
// Emits a `chat-switcher:change` CustomEvent { detail: { key } } when the
// selection changes. Renders in light DOM so Tailwind classes apply.

const ORB_SVG = `
<svg viewBox="0 0 48 48" fill="none" class="w-5 h-5" aria-hidden="true">
  <circle cx="24" cy="24" r="20" stroke="currentColor" stroke-opacity="0.22" stroke-width="1.5"/>
  <g stroke="currentColor" stroke-opacity="0.4" stroke-width="1.1">
    <line x1="24" y1="24" x2="24" y2="8"/><line x1="24" y1="24" x2="38" y2="15"/>
    <line x1="24" y1="24" x2="39" y2="32"/><line x1="24" y1="24" x2="24" y2="40"/>
    <line x1="24" y1="24" x2="9" y2="32"/><line x1="24" y1="24" x2="10" y2="15"/>
    <line x1="24" y1="8" x2="38" y2="15"/><line x1="38" y1="15" x2="39" y2="32"/>
    <line x1="9" y1="32" x2="10" y2="15"/><line x1="24" y1="40" x2="39" y2="32"/>
  </g>
  <g fill="currentColor">
    <circle cx="24" cy="8" r="2.3"/><circle cx="38" cy="15" r="2.3"/>
    <circle cx="39" cy="32" r="2.3"/><circle cx="24" cy="40" r="2.3"/>
    <circle cx="9" cy="32" r="2.3"/><circle cx="10" cy="15" r="2.3"/>
  </g>
  <circle cx="24" cy="24" r="7.5" stroke="currentColor" stroke-opacity="0.35" stroke-width="1.1"/>
  <circle cx="24" cy="24" r="4.2" fill="currentColor"/>
</svg>`

class ChatSwitcher extends HTMLElement {
  connectedCallback() {
    if (this._mounted) return
    this._mounted = true

    this.data  = this._readData()
    this.value = this.getAttribute("value") || this._firstKey()
    this.open  = false

    this.render()

    this._onDocClick = (e) => { if (!this.contains(e.target)) this._close() }
    document.addEventListener("click", this._onDocClick)
  }

  disconnectedCallback() {
    document.removeEventListener("click", this._onDocClick)
  }

  _readData() {
    const tag = this.querySelector('script[type="application/json"]')
    try { return JSON.parse(tag?.textContent || "{}") } catch { return { groups: [] } }
  }

  _allItems() {
    return (this.data.groups || []).flatMap((g) => g.items || [])
  }

  _firstKey() {
    return this._allItems()[0]?.key || ""
  }

  _current() {
    return this._allItems().find((i) => i.key === this.value) || this._allItems()[0]
  }

  // Public API used by the drawer controller to keep them in sync.
  setValue(key, { silent = false } = {}) {
    this.value = key
    this._renderTrigger()
    this._renderMenuActive()
    if (!silent) this._emit()
  }

  _emit() {
    this.dispatchEvent(new CustomEvent("chat-switcher:change", {
      detail: { key: this.value }, bubbles: true
    }))
  }

  _avatarHTML(avatar) {
    if (avatar === "orb") return `<span class="text-violet-fg flex items-center justify-center w-6 h-6">${ORB_SVG}</span>`
    if (typeof avatar === "string" && avatar.startsWith("#")) {
      return `<span class="w-6 h-6 rounded bg-raised border border-edge flex items-center justify-center text-[11px] font-semibold text-ink-soft">${avatar.slice(1)}</span>`
    }
    return `<span class="w-6 h-6 flex items-center justify-center text-lg">${avatar || "💬"}</span>`
  }

  render() {
    this.innerHTML = `
      <div class="relative" data-cs-root>
        <button type="button" data-cs-trigger
          class="w-full flex items-center gap-2 bg-raised border border-edge rounded-lg px-2.5 py-1.5 text-sm text-ink hover:border-violet-500 focus:outline-none focus:border-violet-500 transition-colors">
          <span data-cs-trigger-avatar class="shrink-0"></span>
          <span data-cs-trigger-label class="flex-1 min-w-0 text-left truncate"></span>
          <svg class="w-4 h-4 text-ink-muted shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"/>
          </svg>
        </button>
        <div data-cs-menu
          class="hidden absolute left-0 right-0 mt-1 z-50 max-h-[60vh] overflow-y-auto bg-surface border border-edge rounded-lg shadow-xl py-1">
          ${this._menuHTML()}
        </div>
      </div>`

    this.querySelector("[data-cs-trigger]").addEventListener("click", (e) => {
      e.preventDefault(); e.stopPropagation(); this._toggle()
    })
    this.querySelectorAll("[data-cs-item]").forEach((el) => {
      el.addEventListener("click", (e) => {
        e.preventDefault()
        this.setValue(el.dataset.key)
        this._close()
      })
    })
    this._renderTrigger()
  }

  _menuHTML() {
    return (this.data.groups || []).map((g) => `
      <p class="px-3 pt-1.5 pb-0.5 text-[10px] font-bold uppercase tracking-[0.14em] text-ink-muted">${g.label}</p>
      ${(g.items || []).map((it) => `
        <button type="button" data-cs-item data-key="${it.key}"
          class="w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-raised transition-colors">
          ${this._avatarHTML(it.avatar)}
          <span class="min-w-0 flex-1">
            <span class="block text-sm text-ink truncate">${it.label}</span>
            ${it.sublabel ? `<span class="block text-[10px] text-ink-muted truncate">${it.sublabel}</span>` : ""}
          </span>
          <svg data-cs-check class="w-4 h-4 text-violet-fg shrink-0 ${it.key === this.value ? "" : "invisible"}" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"/>
          </svg>
        </button>`).join("")}
    `).join("")
  }

  _renderTrigger() {
    const cur = this._current()
    if (!cur) return
    this.querySelector("[data-cs-trigger-avatar]").innerHTML = this._avatarHTML(cur.avatar)
    this.querySelector("[data-cs-trigger-label]").textContent = cur.label
  }

  _renderMenuActive() {
    this.querySelectorAll("[data-cs-item]").forEach((el) => {
      const check = el.querySelector("[data-cs-check]")
      if (check) check.classList.toggle("invisible", el.dataset.key !== this.value)
    })
  }

  _toggle() { this.open ? this._close() : this._openMenu() }

  _openMenu() {
    this.open = true
    this.querySelector("[data-cs-menu]").classList.remove("hidden")
  }

  _close() {
    this.open = false
    const menu = this.querySelector("[data-cs-menu]")
    if (menu) menu.classList.add("hidden")
  }
}

if (!customElements.get("chat-switcher")) {
  customElements.define("chat-switcher", ChatSwitcher)
}
